// © Microsoft Corporation. All rights reserved.

import { getContextData, getLocalStorage, RequestContext } from "@msnews/core";
import { getRenderInfoByContent } from "@msnews/social-utils";
import { getLocationHref } from "@msnews/isomorphic-util";
import { MY_REACTION_PREFIX } from "../Constants";
import { ActivityData, Comment, CommentSummary, enums, OneServiceReactionCommentMetadata, ReactionBreakdownReactionCounts, ReactionSummary, SocialBarData, SocialReactions, TopCommentData } from "../schema";
import { getExternalURL } from "../utils/UrlHelper";

export class SocialServiceMapper {
    public static convertContent(contentData) {
        if (!contentData) {
            return null;
        }

        let reactionCounts = {};
        enums.reactionOrder.map(key => {
            if (contentData) {
                if (contentData.reactionSummary) {
                    reactionCounts[key] = (contentData.reactionSummary.subReactionSummaries.find(x => x.type.toLowerCase() == key) || { totalCount: 0 }).totalCount;
                } else {
                    reactionCounts[key] = 0;
                }
            }
        });

        const locationHref = getContextData((new RequestContext()).data.locationHref, "locationHref");
        const renderInfo = getRenderInfoByContent(locationHref, contentData);
        reactionCounts = renderInfo.reactionRenderInfo.blockedReactions ? Object.keys(reactionCounts)
            .filter(reaction => !renderInfo.reactionRenderInfo.blockedReactions.includes(reaction))
            .reduce((obj, key) => { obj[key] = reactionCounts[key]; return obj; }, {}) : reactionCounts;

        return {
            comments: {
                skip: 0,
                top: 0,
                moreCount: 0,
                list: []
            },
            ...contentData,
            ...{
                totalCommentCount: contentData.commentSummary ? contentData.commentSummary.totalCount : 0,
                reactionCounts,
                blockedReactions: renderInfo.reactionRenderInfo.blockedReactions,
                reactionRenderStatus: renderInfo.reactionRenderInfo.renderStatus,
                commentRenderStatus: renderInfo.commentRenderInfo.renderStatus
            }
        };
    }
    public static convertUser(userData) {
        if (!userData) {
            return {
                state: enums.userStateType.anonymous
            };
        } else {
            let state: string;
            if (userData.isSocialUser) {
                state = enums.userStateType.existingUser;
            } else if (userData.id && !userData.id.startsWith("m-")) {
                state = enums.userStateType.newUser;
            } else {
                state = enums.userStateType.anonymous;
            }

            let nameStatus: string = userData.nameStatus || enums.userNameStatusType.normal;
            if (state == enums.userStateType.newUser && (userData.primaryName == null || userData.primaryName.trim() === "")) {
                nameStatus = enums.userNameStatusType.notAllow;
            }

            return {
                ...userData,
                ...{
                    name: userData.primaryName,
                    avatarUrl: userData.notUseAvatar || !userData.primaryAvatar ?
                        undefined :
                        userData.primaryAvatar,
                    likesCount: userData.reactionSummary ? userData.reactionSummary.totalCount : 0,
                    postsCount: userData.commentSummary ? userData.commentSummary.totalCount : 0,
                    followersCount: (userData.followSummary && userData.followSummary.subFollowSummaries.find(x => x.type == "FollowBy") || { totalCount: 0 }).totalCount,
                    followingCount: (userData.followSummary && userData.followSummary.subFollowSummaries.find(x => x.type == "Follow") || { totalCount: 0 }).totalCount,
                    reactionsCount: (userData.reactionSummary && userData.reactionSummary.totalCount) || 0,
                    badge: "Commenter",
                    state: state,
                    avatarStatus: userData.avatarStatus || enums.userAvatarStatusType.normal,
                    nameStatus,
                    ageGroup: userData.ageGroup || enums.userAgeGroup.above18
                }
            };
        }
    }
    public static convertUserList(userListData) {
        if (!userListData) {
            return null;
        }
        return {
            skip: userListData.skip,
            top: userListData.top,
            hasMore: userListData.hasMore,
            moreCount: userListData.totalCount - (userListData.top + userListData.skip),
            totalCount: userListData.totalCount,
            list: userListData.items.map(item => this.convertUser(item.links.find(x => x.type == "User").item || null))
        };
    }

    public static convertComment(commentData) {
        if (!commentData) {
            return null;
        }

        let owner = null;
        let content = null;
        if (commentData.links) {
            const foundUser = commentData.links.find((link) => link.type == "User" && link.item.id == commentData.createdBy);
            owner = foundUser ? SocialServiceMapper.convertUser(foundUser.item) : null;

            const foundContent = commentData.links.find((link) => link.type == "Content");
            content = foundContent ? SocialServiceMapper.convertContent(foundContent.item) : null;
        }

        const isHidden = commentData.visibleStatus != enums.visibleStatusType.Normal;
        const isInReview = commentData.visibleStatus == enums.visibleStatusType.InReview;
        const isDeleted = [enums.visibleStatusType.DeletedByAI, enums.visibleStatusType.DeletedByHI, enums.visibleStatusType.DeletedByUser].includes(commentData.visibleStatus);
        // TODO: Remove this flag when API no longer show this
        const skipRender = isInReview;
        const reactionCounts = {};
        enums.reactionOrder.map(key => {
            if (commentData.reactionSummary) {
                reactionCounts[key] = (commentData.reactionSummary.subReactionSummaries.find(x => x.type.toLowerCase() == key) || { totalCount: 0 }).totalCount;
            } else {
                reactionCounts[key] = 0;
            }
        });

        return {
            ...commentData,
            ...{
                ownerId: commentData.createdBy,
                text: commentData.body.replace(/(?:\r\n|\r|\n)/g, "<br>"),
                createDatetime: commentData.createdTime,
                reactionCounts,
                user: owner,
                content: content,
                isHidden: isHidden,
                isInReview: isInReview,
                isDeleted: isDeleted,
                skipRender: skipRender,
                replies: [],
                replyCount: commentData.commentSummary ? commentData.commentSummary.totalCount : 0
            }
        };
    }
    public static convertCommentList(commentListData) {
        if (!commentListData) {
            return null;
        }
        return {
            commentStatus: "On",
            skip: commentListData.skip,
            top: commentListData.top,
            hasMore: commentListData.hasMore,
            hasPrevious: commentListData.hasPrevious,
            asc: commentListData.asc,
            moreCount: commentListData.totalCount - (commentListData.top - 1 + commentListData.skip - 1 + 2),
            totalCount: commentListData.totalCount,
            list: commentListData.items.map((item) => SocialServiceMapper.convertComment(item))
        };
    }
    public static convertReactionsResponseToReactionBreakdownData(reactionsResponse, content?) {
        if (!reactionsResponse) {
            return null;
        }

        const reactionDetails = reactionsResponse.items;
        let reactionSummary;
        try {
            reactionSummary = reactionsResponse.links.find(x => x.type == "Content" || x.type == "Comment").item.reactionSummary.subReactionSummaries;
        } catch (e) {
            reactionSummary = null;
        }
        const reactionBreakdownData = { reactionCounts: { all: 0 }, items: { all: [] }, content: null };
        enums.reactionOrder.map(key => {
            reactionBreakdownData.reactionCounts[key] = 0;
            reactionBreakdownData.items[key] = [];
        });

        if (reactionSummary) {
            reactionSummary.filter(x => !content?.blockedReactions?.includes(x.type.toLowerCase())).map(x => {
                reactionBreakdownData.reactionCounts[x.type.toLowerCase()] = x.totalCount;
                reactionBreakdownData.reactionCounts.all += x.totalCount;
            });
        }

        if (reactionDetails) {
            reactionDetails.filter(x => !content?.blockedReactions?.includes(x.subType.toLowerCase())).map(x => {
                const rateValue = x.subType.toLowerCase();
                const user = x.links.find(x => x.type == "User") ? x.links.find(x => x.type == "User").item : { state: enums.userStateType.anonymous, primaryName: "You" };
                user.isFollowingFetched = true;
                user.isFollowing = !!x.links.find(x => x.type == "Follow");
                reactionBreakdownData.items[x.subType.toLowerCase()].push({ user: user, rateValue });
                reactionBreakdownData.items.all.push({ user: user, rateValue });
            });
        }

        if (reactionsResponse.links && reactionsResponse.links.length > 0) {
            const contentLinks = reactionsResponse.links.filter(link => link.type.toLowerCase() == "content");
            if (contentLinks.length > 0) {
                reactionBreakdownData.content = contentLinks[0].item;
            }
        }

        return reactionBreakdownData;
    }
    public static convertRatingToRateValue(ratingData) {
        if (ratingData && ratingData.items) {
            ratingData = ratingData.items[0];
        }
        const type = (ratingData && ratingData.subType) ? ratingData.subType.toLowerCase() : null;
        return type;
    }
    public static convertUserActivities(userActivitiesData) {
        if (!userActivitiesData) {
            return null;
        }
        return {
            skip: userActivitiesData.skip,
            top: userActivitiesData.top,
            hasMore: userActivitiesData.hasMore,
            moreCount: userActivitiesData.totalCount - (userActivitiesData.top - 1 + userActivitiesData.skip - 1 + 2),
            totalCount: userActivitiesData.totalCount,
            list: userActivitiesData.items.map(activity => this.convertUserActivity(activity))
        };
    }
    public static convertUserActivity(activity): ActivityData {
        if (!activity) {
            return null;
        }

        const baseActivity = {
            id: activity.id,
            type: activity.type,
            datetime: activity.createdDateTime,
            isSeen: activity.read,
            notification: activity.notification
        };
        switch (activity.type) {
            case enums.activityType.follow: {
                const userLink = activity.links.find(x => x.type == "User");
                return {
                    ...baseActivity,
                    user: this.convertUser(userLink && userLink.item)
                };
            }
            case enums.activityType.badge: {
                const achievementLink = activity.links.find(x => x.type == "Achievement");
                return {
                    ...baseActivity,
                    badgeType: achievementLink && achievementLink.item.subType
                };
            }
            case enums.activityType.reply: {
                const userLink = activity.links.find(x => x.type == "User");
                const contentLink = activity.links.find(x => x.type == "Content");
                const commentLink = activity.links.find(x => x.type == "Comment");
                return {
                    ...baseActivity,
                    user: this.convertUser(userLink && userLink.item),
                    target: {
                        url: contentLink && contentLink.item.url,
                        body: commentLink && commentLink.item.body.replace(/(?:\r\n|\r|\n)/g, "<br>"),
                        comment: commentLink && this.convertComment(commentLink.item)
                    }
                };
            }
            case enums.activityType.reaction: {
                const userLink = activity.links.find(x => x.type == "User");
                const contentLink = activity.links.find(x => x.type == "Content");
                const commentLink = activity.links.find(x => x.type == "Comment");
                const reactionLink = activity.links.find(x => x.type == "Reaction");

                const targetReactionCounts = reactionLink && reactionLink.item.targetType == "Comment" ?
                    this.convertSubReactionSummariesToReactionCounts(commentLink && commentLink.item.reactionSummary && commentLink.item.reactionSummary.subReactionSummaries) :
                    this.convertSubReactionSummariesToReactionCounts(contentLink && contentLink.item.reactionSummary && contentLink.item.reactionSummary.subReactionSummaries);

                const targetBody = reactionLink && reactionLink.item.targetType == "Comment" ?
                    commentLink && commentLink.item.body :
                    contentLink && contentLink.item.title;

                return {
                    ...baseActivity,
                    user: this.convertUser(userLink && userLink.item),
                    target: {
                        url: contentLink && contentLink.item.url,
                        reactionCounts: targetReactionCounts,
                        body: targetBody.replace(/(?:\r\n|\r|\n)/g, "<br>"),
                        comment: commentLink && this.convertComment(commentLink.item)
                    }
                };
            }
            case enums.activityType.moderation: {
                const commentLink = activity.links.find(x => x.type == "Comment");
                const moderationRecordLink = activity.links.find(x => x.type == "ModerationRecord");
                const moderationErrorType = moderationRecordLink && moderationRecordLink.item.subType;
                let message: string;
                switch (moderationErrorType) {
                    case enums.moderationErrorType.takedown:
                        message = "This comment was removed because it didn't meet our guidelines."; break;
                    case enums.moderationErrorType.ban:
                        message = "You've been banned due to violations of our guidelines."; break;
                    case enums.moderationErrorType.notAllowName:
                        message = "Your profile name didn't meet our guidelines."; break;
                    case enums.moderationErrorType.notAllowPicture:
                        message = "Your profile picture didn't meet our guidelines."; break;
                    case enums.moderationErrorType.approveUserAppeal:
                        message = "At your request, we've reviewed your case and reversed the prior decision. You should be able to post again soon. Thank you."; break;
                    case enums.moderationErrorType.approveCommentAppeal:
                        message = "At your request, we've reviewed your post and reversed the decision to remove it. This post will be restored shortly. Thank you."; break;
                    case enums.moderationErrorType.approveProfilePicAppeal:
                        message = "At your request, we've reviewed your profile picture and reversed the decision to remove it. Your profile picture will be restored shortly. Thank you."; break;
                    case enums.moderationErrorType.rejectUserAppeal:
                        message = "At your request, we've reviewed your case and found that your posts violated our community guidelines."; break;
                    case enums.moderationErrorType.rejectCommentAppeal:
                        message = "At your request, we've reviewed your post that was removed and found it violated our community guidelines."; break;
                    case enums.moderationErrorType.rejectProfilePicAppeal:
                        message = "At your request, we've reviewed your case and found that your profile picture violated our community guidelines."; break;
                    default:
                        message = "";
                }
                return {
                    ...baseActivity,
                    moderationErrorType: moderationErrorType,
                    message: message,
                    target: commentLink && {
                        id: commentLink.item.id,
                        body: commentLink.item.body
                    }
                };
            }
        }

        return baseActivity;
    }
    private static convertSubReactionSummariesToReactionCounts(subReactionSummaries): ReactionBreakdownReactionCounts {
        if (!subReactionSummaries) { return null; }
        const reactionCounts = { all: 0 };
        enums.reactionOrder.map(key => {
            reactionCounts[key] = 0;
        });
        subReactionSummaries.map(x => {
            reactionCounts[x.type.toLowerCase()] = x.totalCount;
        });
        return <ReactionBreakdownReactionCounts>reactionCounts;
    }
    public static mapFeedResponseToSocialBarData(id: string, myReaction: string, reactionSummary: ReactionSummary, commentSummary: CommentSummary, topComments?: Array<any>,  localStorageGet?: (id: string) => string): SocialBarData {

        // test hook for injecting mock social reactions
        const locationHref = getLocationHref();
        if (locationHref && locationHref.indexOf("reactionstest") > -1) {
            try {
                reactionSummary = { totalCount: 0, subReactionSummaries: [] };
                commentSummary = { totalCount: 0, subCommentSummaries: [] };
                reactionSummary.totalCount = Math.floor(Math.random() * 10000);
                commentSummary.totalCount = Math.floor(Math.random() * 10000);
                const reactionTypes = ["angry", "like", "love", "sad", "surprised", "thinking"].sort(() => 0.5 - Math.random()).slice(0, Math.floor(Math.random() * 3));
                reactionTypes.forEach(type => reactionSummary.subReactionSummaries.push({ totalCount: Math.floor(Math.random() * 10), type: type }));
            } catch {
                // noop
            }

            // test hook for injecting mock top comments
            if (commentSummary.totalCount > 0) {
                try {
                    const sampleComments = [
                        { id: "82ccfa9b-56ac-41ca-9546-9f3910ec3cec"},
                        { id: "f21a5f91-a6a9-4e80-bb59-3839e296773f"},
                        { id: "c400d1c0-2f0d-469f-986c-8bebe2de1060"},
                        { id: "1be1cb2b-6210-43b4-b962-ce1df9b524d0"},
                        { id: "4e19e02f-f6fc-405d-8f99-1f007ce7f5df"}
                    ];
                    const randomIndex = Math.floor(Math.random() * 5);
                    topComments = sampleComments.slice(randomIndex, randomIndex+1);
                } catch {
                    // noop
                }
            }
        }

        const subReactionSummaries: OneServiceReactionCommentMetadata[] = reactionSummary && reactionSummary.subReactionSummaries ? reactionSummary.subReactionSummaries : [];

        const getSubReactionCounts = (reactionType: SocialReactions) => {
            if (Array.isArray(subReactionSummaries)) {
                const result = subReactionSummaries.filter(value => value.type?.toLowerCase() === (reactionType as string)?.toLowerCase());
                return result && (result.length > 0) ? result[0].totalCount : 0;
            }
        };

        const reactionCounts = {
            angry: getSubReactionCounts(SocialReactions.angry),
            like: getSubReactionCounts(SocialReactions.like),
            love: getSubReactionCounts(SocialReactions.love),
            sad: getSubReactionCounts(SocialReactions.sad),
            surprised: getSubReactionCounts(SocialReactions.surprised),
            thinking: getSubReactionCounts(SocialReactions.thinking)
        };

        const myLocalReaction = !myReaction && SocialServiceMapper.getLocalContentReaction(id, localStorageGet);
        if (myLocalReaction) {
            myReaction = myLocalReaction;
            reactionCounts[myReaction]++;
        }

        const reactionsOptedOut = !reactionSummary;
        const commentsOptedOut = !commentSummary;

        return {
            myReaction,
            id,
            totalCommentsCount: commentSummary && commentSummary.totalCount,
            reactionCounts,
            topComments,
            reactionsOptedOut,
            commentsOptedOut
        };
    }

    public static getLocalContentReaction(id: string, localStorageGet?: (id: string) => string): string {
        if (localStorageGet) {
            return localStorageGet(id);
        }

        if (getLocalStorage().supported && getLocalStorage().getItem(`${MY_REACTION_PREFIX}${id}`)) {
            return getLocalStorage().getItem(`${MY_REACTION_PREFIX}${id}`);
        }

        return null;
    }

    public static convertCommentToTopCommentData (comment: Comment, contentUrl: string, market: string): TopCommentData {
        return !comment || !comment.user ? null :
            {
                market: market,
                username: comment.user.primaryName,
                avatarUrl: comment.user.notUseAvatar || !comment.user.primaryAvatar ?  undefined : comment.user.primaryAvatar,
                profileUrl: getExternalURL(`${enums.externalUrls.profilePage}${comment.user.id}`, market),
                commentUrl: getExternalURL(contentUrl, market, comment.id),
                commentBody: comment.text,
                replyCount: comment.replyCount,
                reactionCounts: comment.reactionCounts,
                createDatetime: comment.createDatetime
            };
    }

    public static convertPublisherFeed (publisherFeedResponse: any) {
        if (!publisherFeedResponse?.sections) { return; }
        const cards = publisherFeedResponse?.sections[0]?.cards;

        if (!cards && cards.length < 1) { return; }
        const provider = cards[0]?.provider;

        return {
            items: cards[0]?.subCards.map(subCard => {
                return {
                    ...subCard,
                    provider
                };
            })
        };
    }
}